// ────────────────────────────────────────────────────────────────
//  Deskte — Beta Onboarding Module
//  Three pieces:
//    1. BetaInviteButton  — opens a dialog to generate one-shot invite links
//    2. BetaOnboardScreen — shown when the URL contains ?beta=TOKEN;
//                           creates the user and logs them in
//    3. OnboardingTour    — 6-step wizard shown once per user after login
//
//  Everything reads/writes localStorage under keys prefixed `dkt6_`.
//  No backend required.
// ────────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import {
  X, Copy, Check, Users, Gift, ChevronRight, ChevronLeft,
  FileText, DollarSign, Upload, Activity, Rocket, Sparkles,
  Zap, Layers, UserPlus,
} from "lucide-react";

// ─── Storage keys ───────────────────────────────────────────────
export const BETA_TOKENS_KEY = "dkt6_beta_tokens";
export const ONBOARDED_PREFIX = "dkt6_onboarded_";

// ─── Utils ──────────────────────────────────────────────────────
const uid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID().replace(/-/g, "").slice(0, 12)
    : Math.random().toString(36).slice(2, 14));

const readTokens = () => {
  try {
    return JSON.parse(localStorage.getItem(BETA_TOKENS_KEY) || "[]");
  } catch {
    return [];
  }
};
const writeTokens = (arr) => {
  localStorage.setItem(BETA_TOKENS_KEY, JSON.stringify(arr));
};

/**
 * Creates a new single-use invite token stored in localStorage and
 * returns the full URL that can be shared with the invitee.
 */
export const createBetaInvite = ({
  role = "agent",
  agencyId = "a1",
  parentAgentId = null,
  createdBy,
  note = "",
}) => {
  const token = uid();
  const entry = {
    token,
    role,
    agencyId,
    parentAgentId,
    createdBy,
    note,
    createdAt: new Date().toISOString(),
    used: false,
  };
  const all = readTokens();
  all.push(entry);
  writeTokens(all);
  const base =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}`
      : "";
  return { token, entry, url: `${base}?beta=${token}` };
};

/**
 * Consumes a token if it exists and is unused. Returns the token entry
 * or null if invalid. Marks the token as used on success.
 */
export const consumeBetaToken = (token) => {
  const all = readTokens();
  const idx = all.findIndex((t) => t.token === token && !t.used);
  if (idx < 0) return null;
  all[idx].used = true;
  all[idx].usedAt = new Date().toISOString();
  writeTokens(all);
  return all[idx];
};

/** True when a token exists and has not been used. */
export const peekBetaToken = (token) => {
  const all = readTokens();
  return all.find((t) => t.token === token && !t.used) || null;
};

export const hasOnboarded = (userId) =>
  !!localStorage.getItem(ONBOARDED_PREFIX + userId);
export const markOnboarded = (userId) =>
  localStorage.setItem(ONBOARDED_PREFIX + userId, "1");
export const resetOnboarding = (userId) =>
  localStorage.removeItem(ONBOARDED_PREFIX + userId);

// ────────────────────────────────────────────────────────────────
//  i18n (self-contained)
// ────────────────────────────────────────────────────────────────
const T = {
  en: {
    inviteBtn: "Beta Invite",
    inviteTitle: "Generate Beta Invite Link",
    inviteDesc:
      "Create a one-time link to invite a beta tester. They'll set their name and log in automatically.",
    inviteAs: "Invite as",
    roleAgent: "Agent",
    roleAssistant: "Seat",
    inviteNote: "Note (optional)",
    notePlaceholder: "e.g. Tester from ACME Insurance",
    generate: "Generate Invite Link",
    generated: "Invite link created",
    copied: "Copied!",
    copy: "Copy link",
    close: "Close",
    shareVia:
      "Share this link with your beta tester. It works once — after they sign in, it is consumed.",
    welcomeBeta: "Welcome to Deskte Beta",
    welcomeBetaSub:
      "You've been invited to try Deskte. Tell us your name to get started.",
    yourName: "Your name",
    yourNamePh: "e.g. Alex Martinez",
    letsGo: "Let's go →",
    invalidToken: "This invite link is invalid or already used.",
    goHome: "Go to sign in",

    tourSkip: "Skip tour",
    tourNext: "Next",
    tourBack: "Back",
    tourDone: "Start using Deskte",
    step: "Step",
    of: "of",

    s1Title: "Welcome, {name}",
    s1Body:
      "Let's take a 60-second tour of Deskte. You'll see the 5 modules that agents use every day.",

    s2Title: "Clients — your CRM core",
    s2Body:
      "Track every client with family, policies, renewals, and notes. Search and filter by status, carrier, or policy type.",

    s3Title: "Policies — 18 types in one view",
    s3Body:
      "Health, Medicare, Life, Auto, Home, Commercial, Pet, Travel and more. Everything unified — stop switching between systems.",

    s4Title: "Commissions — where Deskte pays for itself",
    s4Body:
      "Upload PDF, Excel or CSV statements. The smart matcher pairs every payment with the right client at 92% accuracy.",

    s5Title: "Doc Requests & E-Signatures",
    s5Body:
      "Send a link, your client uploads a photo from their phone. Need a SOA or HIPAA form signed? Done in seconds.",

    s6Title: "You're ready",
    s6Body:
      "You already have demo clients and policies so you can explore. When you're ready, create your first real client.",
    s6Explore: "Explore demo data",
    s6Reset: "Clear demo data & start fresh",
    s6Resetting: "Clearing…",
    s6DidReset: "Demo data cleared ✓",
  },
  es: {
    inviteBtn: "Invitar Beta",
    inviteTitle: "Generar Link de Invitación Beta",
    inviteDesc:
      "Crea un link de un solo uso para invitar a un beta tester. Entrarán automáticamente al poner su nombre.",
    inviteAs: "Invitar como",
    roleAgent: "Agente",
    roleAssistant: "Asiento",
    inviteNote: "Nota (opcional)",
    notePlaceholder: "ej. Tester de Aseguradora ACME",
    generate: "Generar Link",
    generated: "Link creado",
    copied: "¡Copiado!",
    copy: "Copiar link",
    close: "Cerrar",
    shareVia:
      "Comparte este link con tu beta tester. Funciona una sola vez — al entrar, se consume.",
    welcomeBeta: "Bienvenido a Deskte Beta",
    welcomeBetaSub:
      "Te han invitado a probar Deskte. Dinos tu nombre para comenzar.",
    yourName: "Tu nombre",
    yourNamePh: "ej. Alex Martínez",
    letsGo: "Vamos →",
    invalidToken: "Este link de invitación no es válido o ya fue usado.",
    goHome: "Ir al login",

    tourSkip: "Saltar tour",
    tourNext: "Siguiente",
    tourBack: "Atrás",
    tourDone: "Empezar a usar Deskte",
    step: "Paso",
    of: "de",

    s1Title: "Bienvenido, {name}",
    s1Body:
      "Te damos un tour de 60 segundos por Deskte. Verás los 5 módulos que los agentes usan a diario.",

    s2Title: "Clientes — el corazón del CRM",
    s2Body:
      "Gestiona cada cliente con familia, pólizas, renovaciones y notas. Busca y filtra por estado, aseguradora o tipo de póliza.",

    s3Title: "Pólizas — 18 tipos en una sola vista",
    s3Body:
      "Salud, Medicare, Vida, Auto, Hogar, Comercial, Mascotas, Viaje y más. Todo unificado — deja de saltar entre sistemas.",

    s4Title: "Comisiones — aquí Deskte se paga solo",
    s4Body:
      "Sube estados de cuenta en PDF, Excel o CSV. El matcher inteligente empareja cada pago con el cliente correcto (92% de precisión).",

    s5Title: "Documentos y Firmas Digitales",
    s5Body:
      "Envía un link, el cliente sube una foto desde su teléfono. ¿Necesitas un SOA o HIPAA firmado? Listo en segundos.",

    s6Title: "Estás listo",
    s6Body:
      "Ya tienes clientes y pólizas de demo para explorar. Cuando quieras, crea tu primer cliente real.",
    s6Explore: "Explorar datos de demo",
    s6Reset: "Borrar datos de demo y empezar limpio",
    s6Resetting: "Borrando…",
    s6DidReset: "Datos de demo borrados ✓",
  },
};

const tr = (lang, key, vars = {}) => {
  const base = (T[lang] || T.en)[key] || T.en[key] || key;
  return base.replace(/\{(\w+)\}/g, (_, k) => vars[k] ?? "");
};

// ────────────────────────────────────────────────────────────────
//  BetaInviteButton — opens the invite generator dialog
// ────────────────────────────────────────────────────────────────
export const BetaInviteButton = ({ currentUser, lang = "en" }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 5,
          background: "linear-gradient(135deg,#ff6b9d22,#ff6b9d11)",
          border: "1px solid rgba(255,107,157,.4)",
          color: "#ff6b9d",
          fontSize: ".75rem",
          fontWeight: 700,
          padding: ".45rem .85rem",
          borderRadius: 9,
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        <Gift size={14} /> {tr(lang, "inviteBtn")}
      </button>
      {open && (
        <BetaInviteDialog
          currentUser={currentUser}
          lang={lang}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
};

const BetaInviteDialog = ({ currentUser, lang, onClose }) => {
  const [role, setRole] = useState("agent");
  const [note, setNote] = useState("");
  const [generated, setGenerated] = useState(null);
  const [copied, setCopied] = useState(false);

  const generate = () => {
    const invite = createBetaInvite({
      role,
      agencyId: currentUser?.agencyId || "a1",
      parentAgentId: role === "assistant" ? currentUser?.id : null,
      createdBy: currentUser?.id,
      note,
    });
    setGenerated(invite);
  };

  const copyUrl = async () => {
    if (!generated) return;
    try {
      await navigator.clipboard.writeText(generated.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {}
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 300,
        background: "rgba(0,0,0,.75)",
        backdropFilter: "blur(6px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--c-surface,#0d1c2e)",
          border: "1px solid rgba(255,107,157,.4)",
          borderRadius: 16,
          padding: "1.5rem",
          maxWidth: 480,
          width: "100%",
          boxShadow: "0 24px 80px rgba(0,0,0,.6), 0 0 60px rgba(255,107,157,.15)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: ".85rem",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <div
              style={{
                width: 34,
                height: 34,
                borderRadius: 10,
                background:
                  "linear-gradient(135deg,#ff6b9d,#c850c0)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 0 20px #ff6b9d55",
              }}
            >
              <Gift size={18} color="#fff" />
            </div>
            <div
              style={{
                fontWeight: 800,
                fontSize: "1.02rem",
                color: "var(--c-white,#eef6ff)",
              }}
            >
              {tr(lang, "inviteTitle")}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "var(--c-text3,#45688a)",
              cursor: "pointer",
              padding: 4,
            }}
          >
            <X size={16} />
          </button>
        </div>

        {!generated ? (
          <>
            <p
              style={{
                fontSize: ".78rem",
                color: "var(--c-text2,#7aa3c0)",
                margin: "0 0 1rem",
                lineHeight: 1.5,
              }}
            >
              {tr(lang, "inviteDesc")}
            </p>

            <div style={{ marginBottom: ".85rem" }}>
              <label
                style={{
                  fontSize: ".6rem",
                  color: "var(--c-text3,#45688a)",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: ".08em",
                  display: "block",
                  marginBottom: 5,
                }}
              >
                {tr(lang, "inviteAs")}
              </label>
              <div style={{ display: "flex", gap: ".4rem" }}>
                {["agent", "assistant"].map((r) => {
                  const active = role === r;
                  const color = r === "agent" ? "#00ff88" : "#818cf8";
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setRole(r)}
                      style={{
                        flex: 1,
                        padding: ".55rem",
                        borderRadius: 9,
                        border: `1px solid ${
                          active ? color + "66" : "var(--c-border,#1a3550)"
                        }`,
                        background: active
                          ? color + "14"
                          : "var(--c-surface2,#112538)",
                        color: active ? color : "var(--c-text3,#45688a)",
                        fontSize: ".76rem",
                        fontWeight: 700,
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      {tr(lang, r === "agent" ? "roleAgent" : "roleAssistant")}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ marginBottom: "1.15rem" }}>
              <label
                style={{
                  fontSize: ".6rem",
                  color: "var(--c-text3,#45688a)",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: ".08em",
                  display: "block",
                  marginBottom: 5,
                }}
              >
                {tr(lang, "inviteNote")}
              </label>
              <input
                className="dkt-inp"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={tr(lang, "notePlaceholder")}
                style={{ width: "100%", boxSizing: "border-box" }}
              />
            </div>

            <button
              type="button"
              onClick={generate}
              style={{
                width: "100%",
                padding: ".7rem",
                borderRadius: 10,
                border: "none",
                background: "linear-gradient(135deg,#ff6b9d,#c850c0)",
                color: "#fff",
                fontWeight: 800,
                fontSize: ".86rem",
                cursor: "pointer",
                fontFamily: "inherit",
                letterSpacing: ".02em",
                boxShadow: "0 8px 24px rgba(255,107,157,.3)",
              }}
            >
              {tr(lang, "generate")}
            </button>
          </>
        ) : (
          <>
            <div
              style={{
                background: "rgba(0,255,136,.08)",
                border: "1px solid rgba(0,255,136,.3)",
                borderRadius: 10,
                padding: ".75rem",
                marginBottom: ".85rem",
              }}
            >
              <div
                style={{
                  fontSize: ".72rem",
                  fontWeight: 700,
                  color: "#00ff88",
                  marginBottom: 4,
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <Check size={13} /> {tr(lang, "generated")}
              </div>
              <div
                style={{
                  fontSize: ".72rem",
                  color: "var(--c-text2,#7aa3c0)",
                  lineHeight: 1.5,
                }}
              >
                {tr(lang, "shareVia")}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: ".4rem",
                marginBottom: ".85rem",
              }}
            >
              <input
                readOnly
                value={generated.url}
                onFocus={(e) => e.target.select()}
                style={{
                  flex: 1,
                  padding: ".6rem .7rem",
                  background: "var(--c-surface2,#112538)",
                  border: "1px solid var(--c-border,#1a3550)",
                  borderRadius: 9,
                  color: "var(--c-cyan,#00d4ff)",
                  fontFamily: "var(--font-mono,monospace)",
                  fontSize: ".7rem",
                  fontWeight: 600,
                  boxSizing: "border-box",
                  minWidth: 0,
                }}
              />
              <button
                type="button"
                onClick={copyUrl}
                style={{
                  padding: ".6rem .85rem",
                  borderRadius: 9,
                  border: "1px solid rgba(0,212,255,.4)",
                  background: "rgba(0,212,255,.1)",
                  color: "var(--c-cyan,#00d4ff)",
                  fontWeight: 700,
                  fontSize: ".72rem",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  fontFamily: "inherit",
                  whiteSpace: "nowrap",
                }}
              >
                {copied ? <Check size={13} /> : <Copy size={13} />}
                {copied ? tr(lang, "copied") : tr(lang, "copy")}
              </button>
            </div>

            <button
              type="button"
              onClick={onClose}
              style={{
                width: "100%",
                padding: ".6rem",
                borderRadius: 9,
                border: "1px solid var(--c-border,#1a3550)",
                background: "var(--c-surface2,#112538)",
                color: "var(--c-text2,#7aa3c0)",
                fontWeight: 700,
                fontSize: ".78rem",
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              {tr(lang, "close")}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
//  BetaOnboardScreen — landing page when URL contains ?beta=TOKEN
// ────────────────────────────────────────────────────────────────
export const BetaOnboardScreen = ({ token, users, setUsers, onLogin, lang = "en" }) => {
  const [entry] = useState(() => peekBetaToken(token));
  const [name, setName] = useState("");

  if (!entry) {
    return (
      <div
        style={{
          minHeight: "100vh",
          background: "#070d14",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "1rem",
          fontFamily: "system-ui,sans-serif",
          color: "#c8dff0",
        }}
      >
        <div
          style={{
            background: "#0d1c2e",
            border: "1px solid #1a3550",
            borderRadius: 16,
            padding: "2rem 2.25rem",
            maxWidth: 420,
            textAlign: "center",
          }}
        >
          <div style={{ fontSize: "2rem", marginBottom: ".75rem" }}>⚠️</div>
          <div style={{ fontWeight: 800, fontSize: "1.1rem", marginBottom: ".5rem", color: "#eef6ff" }}>
            {tr(lang, "invalidToken")}
          </div>
          <a
            href={window.location.pathname}
            style={{
              display: "inline-block",
              marginTop: "1rem",
              padding: ".6rem 1.2rem",
              borderRadius: 9,
              background: "linear-gradient(135deg,#00d4ff,#00a3cc)",
              color: "#070d14",
              fontWeight: 800,
              textDecoration: "none",
              fontSize: ".85rem",
            }}
          >
            {tr(lang, "goHome")}
          </a>
        </div>
      </div>
    );
  }

  const submit = () => {
    if (!name.trim()) return;
    // Consume the token (single use)
    consumeBetaToken(token);

    // Create a new user from the invite template
    const color =
      entry.role === "agent"
        ? "#06d6a0"
        : entry.role === "assistant"
        ? "#f472b6"
        : "#1d6fa4";
    const parts = name.trim().split(/\s+/);
    const initials = (
      (parts[0]?.[0] || "") + (parts[1]?.[0] || "")
    ).toUpperCase() || "BT";
    const newUser = {
      id: "u" + uid().slice(0, 6),
      name: name.trim(),
      email: "",
      role: entry.role,
      pin: "0000",
      color,
      initials,
      agencyId: entry.agencyId || "a1",
      parentAgentId: entry.parentAgentId || null,
      phone: "",
      dob: "",
      address: "",
      hireDate: new Date().toISOString().slice(0, 10),
      npn: "",
      title: entry.role === "agent" ? "Licensed Insurance Agent" : "Beta Tester",
      companyName: "",
      companyLogo: "",
      website: "",
      bio: "",
      licenseStates: "",
      isBetaTester: true,
      betaNote: entry.note || "",
    };
    setUsers((prev) => [...prev, newUser]);

    // Clean the URL so a refresh doesn't try to consume again
    if (typeof window !== "undefined" && window.history?.replaceState) {
      window.history.replaceState({}, "", window.location.pathname);
    }

    onLogin(newUser);
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(1200px 800px at 50% -10%, rgba(0,212,255,.18), transparent 60%), #070d14",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        fontFamily: "system-ui,sans-serif",
        color: "#c8dff0",
      }}
    >
      <div
        style={{
          background: "rgba(13,28,46,.92)",
          border: "1px solid rgba(0,212,255,.35)",
          borderRadius: 20,
          padding: "2.25rem 2.25rem 2rem",
          maxWidth: 480,
          width: "100%",
          boxShadow:
            "0 24px 80px rgba(0,0,0,.6), 0 0 80px rgba(0,212,255,.1) inset",
          backdropFilter: "blur(8px)",
        }}
      >
        <div
          style={{
            width: 60,
            height: 60,
            borderRadius: 16,
            background: "linear-gradient(135deg,#00d4ff,#00a3cc)",
            margin: "0 auto 1rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 0 40px rgba(0,212,255,.4)",
          }}
        >
          <Sparkles size={26} color="#070d14" />
        </div>
        <div
          style={{
            fontWeight: 800,
            fontSize: "1.5rem",
            textAlign: "center",
            color: "#eef6ff",
            marginBottom: ".35rem",
            letterSpacing: ".01em",
          }}
        >
          {tr(lang, "welcomeBeta")}
        </div>
        <div
          style={{
            fontSize: ".88rem",
            color: "#7aa3c0",
            textAlign: "center",
            marginBottom: "1.5rem",
            lineHeight: 1.55,
          }}
        >
          {tr(lang, "welcomeBetaSub")}
        </div>

        <label
          style={{
            fontSize: ".62rem",
            color: "#45688a",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: ".08em",
            display: "block",
            marginBottom: 6,
          }}
        >
          {tr(lang, "yourName")}
        </label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder={tr(lang, "yourNamePh")}
          autoFocus
          style={{
            width: "100%",
            padding: ".85rem 1rem",
            background: "#112538",
            border: "1px solid #1a3550",
            borderRadius: 10,
            color: "#eef6ff",
            fontSize: "1rem",
            boxSizing: "border-box",
            marginBottom: "1.25rem",
            fontFamily: "inherit",
            outline: "none",
          }}
        />

        <button
          onClick={submit}
          disabled={!name.trim()}
          style={{
            width: "100%",
            padding: ".9rem",
            borderRadius: 12,
            border: "none",
            background: name.trim()
              ? "linear-gradient(135deg,#00d4ff,#00a3cc)"
              : "#1a3550",
            color: name.trim() ? "#070d14" : "#45688a",
            fontWeight: 800,
            fontSize: "1rem",
            cursor: name.trim() ? "pointer" : "not-allowed",
            fontFamily: "inherit",
            letterSpacing: ".02em",
            boxShadow: name.trim()
              ? "0 12px 30px rgba(0,212,255,.3)"
              : "none",
            transition: "all .2s",
          }}
        >
          {tr(lang, "letsGo")}
        </button>

        <div
          style={{
            marginTop: "1.25rem",
            fontSize: ".65rem",
            color: "#45688a",
            textAlign: "center",
            fontFamily: "var(--font-mono,monospace)",
            letterSpacing: ".06em",
          }}
        >
          role: <b style={{ color: "#00d4ff" }}>{entry.role.toUpperCase()}</b>
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
//  OnboardingTour — 6-step wizard after first login
// ────────────────────────────────────────────────────────────────
const STEP_ICONS = [Rocket, Users, Layers, DollarSign, Upload, Zap];
const STEP_COLORS = [
  "#00d4ff",
  "#00ff88",
  "#8b5cf6",
  "#ffb700",
  "#ff6b9d",
  "#00d4ff",
];

export const OnboardingTour = ({
  currentUser,
  lang = "en",
  onClose,
  onNavigate,
  onResetDemo,
}) => {
  const [step, setStep] = useState(0);
  const [resetStatus, setResetStatus] = useState("idle"); // idle|resetting|done

  if (!currentUser) return null;

  const steps = [
    { key: "s1", nav: null },
    { key: "s2", nav: "clients" },
    { key: "s3", nav: "policies" },
    { key: "s4", nav: "commissions" },
    { key: "s5", nav: "docs" },
    { key: "s6", nav: null },
  ];

  const total = steps.length;
  const current = steps[step];
  const Icon = STEP_ICONS[step];
  const accent = STEP_COLORS[step];
  const title = tr(lang, current.key + "Title", { name: currentUser.name });
  const body = tr(lang, current.key + "Body");

  const finish = () => {
    markOnboarded(currentUser.id);
    onClose && onClose();
  };

  const next = () => {
    if (step < total - 1) {
      const ns = step + 1;
      setStep(ns);
      const nav = steps[ns].nav;
      if (nav && onNavigate) onNavigate(nav);
    } else {
      finish();
    }
  };

  const back = () => {
    if (step > 0) {
      const ns = step - 1;
      setStep(ns);
      const nav = steps[ns].nav;
      if (nav && onNavigate) onNavigate(nav);
    }
  };

  const handleReset = () => {
    if (!onResetDemo) return;
    setResetStatus("resetting");
    try {
      onResetDemo();
      setResetStatus("done");
    } catch {
      setResetStatus("idle");
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 400,
        background: "rgba(0,0,0,.78)",
        backdropFilter: "blur(8px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        fontFamily: "system-ui,sans-serif",
      }}
    >
      <div
        style={{
          background: "linear-gradient(180deg,#0d1c2e,#081525)",
          border: `1px solid ${accent}55`,
          borderRadius: 20,
          padding: "2rem 2.25rem 1.5rem",
          maxWidth: 540,
          width: "100%",
          boxShadow: `0 24px 80px rgba(0,0,0,.7), 0 0 80px ${accent}22`,
          color: "#c8dff0",
          position: "relative",
        }}
      >
        <button
          onClick={finish}
          style={{
            position: "absolute",
            top: 14,
            right: 14,
            background: "none",
            border: "none",
            color: "#45688a",
            cursor: "pointer",
            fontSize: ".7rem",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: ".06em",
            fontFamily: "inherit",
          }}
        >
          {tr(lang, "tourSkip")} ✕
        </button>

        {/* Step indicator */}
        <div
          style={{
            display: "flex",
            gap: 6,
            marginBottom: "1.25rem",
            marginTop: 6,
          }}
        >
          {steps.map((_, i) => (
            <div
              key={i}
              style={{
                flex: 1,
                height: 3,
                borderRadius: 2,
                background: i <= step ? accent : "#1a3550",
                transition: "background .3s",
              }}
            />
          ))}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 14,
            marginBottom: ".85rem",
          }}
        >
          <div
            style={{
              width: 52,
              height: 52,
              borderRadius: 14,
              background: `linear-gradient(135deg,${accent},${accent}88)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: `0 0 30px ${accent}66`,
            }}
          >
            <Icon size={24} color="#070d14" />
          </div>
          <div>
            <div
              style={{
                fontSize: ".6rem",
                color: "#45688a",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: ".1em",
                fontFamily: "var(--font-mono,monospace)",
              }}
            >
              {tr(lang, "step")} {step + 1} {tr(lang, "of")} {total}
            </div>
            <div
              style={{
                fontSize: "1.28rem",
                fontWeight: 800,
                color: "#eef6ff",
                letterSpacing: ".01em",
              }}
            >
              {title}
            </div>
          </div>
        </div>

        <p
          style={{
            fontSize: ".92rem",
            color: "#7aa3c0",
            lineHeight: 1.6,
            margin: "0 0 1.5rem",
          }}
        >
          {body}
        </p>

        {/* Last step extras */}
        {step === total - 1 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: ".5rem",
              marginBottom: "1.25rem",
            }}
          >
            <button
              type="button"
              onClick={handleReset}
              disabled={resetStatus !== "idle"}
              style={{
                padding: ".6rem",
                borderRadius: 9,
                border: "1px solid #1a3550",
                background: "#112538",
                color:
                  resetStatus === "done" ? "#00ff88" : "#7aa3c0",
                fontSize: ".76rem",
                fontWeight: 700,
                cursor: resetStatus === "idle" ? "pointer" : "default",
                fontFamily: "inherit",
              }}
            >
              {resetStatus === "resetting"
                ? tr(lang, "s6Resetting")
                : resetStatus === "done"
                ? tr(lang, "s6DidReset")
                : tr(lang, "s6Reset")}
            </button>
          </div>
        )}

        <div style={{ display: "flex", gap: ".5rem" }}>
          {step > 0 && (
            <button
              onClick={back}
              style={{
                padding: ".7rem 1rem",
                borderRadius: 10,
                border: "1px solid #1a3550",
                background: "#112538",
                color: "#7aa3c0",
                fontWeight: 700,
                fontSize: ".82rem",
                cursor: "pointer",
                fontFamily: "inherit",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <ChevronLeft size={14} /> {tr(lang, "tourBack")}
            </button>
          )}
          <button
            onClick={next}
            style={{
              flex: 1,
              padding: ".85rem",
              borderRadius: 10,
              border: "none",
              background: `linear-gradient(135deg,${accent},${accent}aa)`,
              color: "#070d14",
              fontWeight: 800,
              fontSize: ".92rem",
              cursor: "pointer",
              fontFamily: "inherit",
              letterSpacing: ".02em",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 6,
              boxShadow: `0 10px 28px ${accent}44`,
            }}
          >
            {step < total - 1 ? tr(lang, "tourNext") : tr(lang, "tourDone")}
            {step < total - 1 && <ChevronRight size={16} />}
          </button>
        </div>
      </div>
    </div>
  );
};

// ────────────────────────────────────────────────────────────────
//  Hook: useBetaTokenFromUrl — reads ?beta= from URL once
// ────────────────────────────────────────────────────────────────
export const useBetaTokenFromUrl = () => {
  const [token, setToken] = useState(() => {
    if (typeof window === "undefined") return null;
    try {
      return new URLSearchParams(window.location.search).get("beta");
    } catch {
      return null;
    }
  });
  return [token, setToken];
};
